import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Paperclip, Upload, Download, Trash2 } from 'lucide-react';
import '../nativeitems/NativeItems.css';

// Reusable "Attachments" tab — the native replacement for NetSuite's file
// cabinet on a record (task #69). Drop it onto any native record page:
//
//   <AttachmentsTab recordType="sales_order" recordId={id} />
//
// Sibling of <RecordHistory /> (#53) and <RelatedRecords /> (#55). Talks to the
// four /api/attachments endpoints (attachmentsRoutes.js):
//   GET    /api/attachments/:recordType/:id                list
//   POST   /api/attachments/:recordType/:id                upload (field "file")
//   GET    /api/attachments/:recordType/:id/:aid/url       short-lived signed URL
//   DELETE /api/attachments/:recordType/:id/:aid           soft remove
//
// recordType must be one of RUN_ATTACHMENT_TYPES (packages/attachments):
// sales_order | work_order | purchase_order | item | customer | vendor (plus the
// ns_* export types). recordId is the same id the History/Related tabs use.
//
// Downloads: we request the signed URL on click and window.open it — it expires
// in minutes and is never cached in state (per the seam contract).

const MAX_BYTES = 25 * 1024 * 1024; // mirror of the server's multer cap

function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

const fmtStamp = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function AttachmentsTab({ recordType, recordId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const base = `/api/attachments/${encodeURIComponent(recordType)}/${encodeURIComponent(recordId)}`;

  const load = useCallback(async () => {
    if (!recordType || !recordId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(base);
      setRows(data.attachments || []);
      setNote(data.note || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [base, recordType, recordId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (file.size > MAX_BYTES) {
      window.alert(`"${file.name}" is ${fmtBytes(file.size)} — the limit is ${fmtBytes(MAX_BYTES)}.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(base, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const download = async (row) => {
    try {
      const { data } = await axios.get(`${base}/${encodeURIComponent(row.id)}/url`);
      const url = data.signedUrl || data.url || data.signedURL;
      if (url) window.open(url, '_blank', 'noopener');
      else window.alert('No download URL was returned.');
    } catch (err) {
      window.alert(err.response?.data?.error || err.message);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove "${row.file_name || 'this file'}"? It is soft-removed — the file is retained but hidden.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${base}/${encodeURIComponent(row.id)}`);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nit-card">
      <div className="nit-hist-head">
        <h3><Paperclip size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Attachments</h3>
        <label className="nit-btn" style={{ cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.45 : 1 }}>
          <Upload size={14} /> {busy ? 'Working…' : 'Upload file'}
          <input
            ref={fileRef}
            type="file"
            onChange={onPick}
            disabled={busy}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      {loading && <div className="nit-loading">Loading attachments…</div>}
      {error && <div className="nit-error">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="nit-empty">
          No files attached to this record yet.
          <br />
          {note || 'Upload a file above (25 MB max) — it is stored privately and downloaded through short-lived signed links.'}
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="nit-table-wrap">
          <table className="nit-table">
            <thead>
              <tr>
                <th>File</th>
                <th className="r">Size</th>
                <th>Uploaded by</th>
                <th>Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.file_name || '(unnamed)'}</td>
                  <td className="r nit-muted">{fmtBytes(row.byte_size ?? row.size_bytes)}</td>
                  <td className="nit-muted">{row.uploaded_by_email || row.uploaded_by || '—'}</td>
                  <td className="nit-muted">{fmtStamp(row.created_at || row.uploaded_at)}</td>
                  <td className="r">
                    <button className="nit-btn" onClick={() => download(row)} title="Download (opens a short-lived signed link)">
                      <Download size={13} /> Download
                    </button>{' '}
                    <button className="nit-btn" onClick={() => remove(row)} disabled={busy} title="Soft-remove (file retained, hidden from this list)">
                      <Trash2 size={13} /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <p className="nit-caption">
          {rows.length} file{rows.length === 1 ? '' : 's'}. Stored in a private bucket; downloads use signed links that expire in minutes.
        </p>
      )}
    </div>
  );
}
